import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from "rxjs";
import { ICountry } from "../modules/admin/geo-audience/interfaces/geo-audience-countru.types";

// localStorage keys of the organization selected in the header selector. The
// "super_admin" prefix is historical: owners (allowlisted organizations) use the
// same keys now. Kept for compatibility with stored sessions.
const SUPER_ADMIN_ORGANIZATION_ID_KEY = "super_admin_organization_id";
const SUPER_ADMIN_ORGANIZATION_NAME_KEY = "super_admin_organization_name";

export type AppRole = "admin" | "owner" | "editor" | "super-admin" | "growth";
export interface AppConfigInterface {
    role?: AppRole;
    organization?: string;
    credits?: null | number;
    countries?: ICountry[];
    /** True while the backend is honouring X-Organization-ID for this user. */
    impersonating?: boolean;
    /**
     * Whether the organization selector should be offered: super-admins and
     * owners (role `owner`) of an allowlisted organization (APPCELERATE), who
     * may switch to any organization without the rest of super-admin. Decided
     * by the backend; the SPA never infers it from the role alone.
     */
    can_impersonate?: boolean;

    /** Plan 31 · R1 — the backend flag, true only for a super-admin. */
    can_manage_pricing?: boolean;

    // Dejamos en modo cajon de sastre para que entre cualquier propiedad
    [configKey: string]: any;
}

@Injectable({
    providedIn: 'root'
})
export class AppConfigService {
    private config$: BehaviorSubject<AppConfigInterface> = new BehaviorSubject<AppConfigInterface>({})
    public config: Observable<AppConfigInterface> = this.config$.asObservable();

    public set(config: AppConfigInterface): void {
        this.config$.next(config);
    }

    public get(property: keyof AppConfigInterface): any {
        return this.config?.[property];
    }

    public hasRole(role: AppRole): boolean {
        return this.config$.value?.role === role;
    }

    /** Has an App-Configuration header been received yet (i.e. is the config more than the empty default)? */
    public isLoaded(): boolean {
        return this.config$.value?.role !== undefined;
    }

    /**
     * May the current user switch organization context (organization selector)?
     * Backend-decided flag; super-admin is kept as a fallback for responses
     * from an API that predates `can_impersonate`.
     */
    public canImpersonate(): boolean {
        return this.config$.value?.can_impersonate === true || this.hasRole("super-admin");
    }

    /**
     * Plan 31 · R1 — el pricing lo gestiona solo el super-admin. Se respeta `can_manage_pricing`
     * del header por si llega sin rol, pero el backend solo lo pone a true para un super-admin.
     */
    public canManagePricing(): boolean {
        const config = this.config$.value;
        return config?.role === "super-admin" || config?.can_manage_pricing === true;
    }

    public get countries(): ICountry[] {
        return this.config$.value?.countries || [];
    }

    /** Organization ID selected in the header selector (super-admin or owner; persisted in localStorage; used as X-Organization-ID header). */
    public getSelectedOrganizationId(): string | null {
        const value = localStorage.getItem(SUPER_ADMIN_ORGANIZATION_ID_KEY);
        return value || null;
    }

    /** Organization name selected in the header selector (used for branding/theme by name). */
    public getSelectedOrganizationName(): string | null {
        return localStorage.getItem(SUPER_ADMIN_ORGANIZATION_NAME_KEY) || null;
    }

    /** Set the selected organization and persist id + name. Caller typically reloads the page. */
    public setSelectedOrganization(orgId: string | null, orgName: string | null): void {
        if (orgId) {
            localStorage.setItem(SUPER_ADMIN_ORGANIZATION_ID_KEY, orgId);
            if (orgName != null && orgName.trim()) {
                localStorage.setItem(SUPER_ADMIN_ORGANIZATION_NAME_KEY, orgName.trim());
            } else {
                localStorage.removeItem(SUPER_ADMIN_ORGANIZATION_NAME_KEY);
            }
        } else {
            localStorage.removeItem(SUPER_ADMIN_ORGANIZATION_ID_KEY);
            localStorage.removeItem(SUPER_ADMIN_ORGANIZATION_NAME_KEY);
        }
    }

    /** @deprecated Use setSelectedOrganization(id, name). Kept for backwards compatibility. */
    public setSelectedOrganizationId(orgId: string | null): void {
        const name = this.getSelectedOrganizationName();
        this.setSelectedOrganization(orgId, orgId ? name : null);
    }
}
